import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    FaBoxes,
    FaTachometerAlt,
    FaStore,
    FaUsers,
    FaTruck,
    FaUserFriends,
    FaBoxOpen,
    FaCashRegister,
    FaClipboardList,
    FaBalanceScale,
    FaMoneyBillWave,
    FaSignOutAlt,
    FaChevronDown,
    FaChevronUp,
    FaTimes
} from "react-icons/fa";

const Layout = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [openMenu, setOpenMenu] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    let admin = null;
    try {
        admin = JSON.parse(localStorage.getItem("admin"));
    } catch (e) {
        admin = null;
    }

    // Navigate and close the mobile sidebar drawer at the same time
    const go = (path) => {
        navigate(path);
        setSidebarOpen(false);
    };

    const logout = () => {
        localStorage.clear();
        setSidebarOpen(false);
        navigate("/");
    };

    // Prevent background scrolling while the mobile drawer is open
    useEffect(() => {
        if (sidebarOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [sidebarOpen]);

    // Auto-close the drawer when the window is resized back to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 1024) {
                setSidebarOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Close drawer with the Escape key (accessibility)
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === "Escape" && sidebarOpen) {
                setSidebarOpen(false);
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [sidebarOpen]);

    // Keyboard Enter/Space activation for sidebar navigation (accessibility)
    const keyActivate = (e, fn) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fn();
        }
    };

    const isActive = (path) => location.pathname === path;
    const adminName = admin?.name || "Admin";
    const initials = adminName
        .split(" ")
        .map((p) => p[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();

    return (
        <div className="dashboard">

            {/* SIDEBAR */}
            <div className={`sidebar${sidebarOpen ? " active" : ""}`}>
                <div className="sidebar-head">
                    <div className="sidebar-brand-badge">
                        <FaStore />
                    </div>

                    <div className="sidebar-brand-text">
                        <h2>POS SYSTEM</h2>
                        <p className="sidebar-subtitle">Business Management</p>
                    </div>

                    <button
                        className="sidebar-close"
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Close menu"
                    >
                        <FaTimes />
                    </button>
                </div>

                <ul>
                    <li className={isActive("/dashboard") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/dashboard")}
                        onKeyDown={(e) => keyActivate(e, () => go("/dashboard"))}>
                        <FaTachometerAlt className="menu-icon" />
                        Dashboard
                    </li>

                    <li className={isActive("/stores") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/stores")}
                        onKeyDown={(e) => keyActivate(e, () => go("/stores"))}>
                        <FaStore className="menu-icon" />
                        Stores
                    </li>

                    <li
                        className={isActive("/users") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/users")}
                        onKeyDown={(e) => keyActivate(e, () => go("/users"))}
                    >
                        <FaUsers className="menu-icon" />
                        Enquiry List
                    </li>

                    <li className={isActive("/stock") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/stock")}
                        onKeyDown={(e) => keyActivate(e, () => go("/stock"))}>
                        <FaBoxes className="menu-icon" />
                        Stock Management
                    </li>

                    <li
                        role="button"
                        tabIndex={0}
                        aria-expanded={openMenu === "suppliers"}
                        onClick={() => setOpenMenu(openMenu === "suppliers" ? null : "suppliers")}
                        onKeyDown={(e) => keyActivate(e, () => setOpenMenu(openMenu === "suppliers" ? null : "suppliers"))}
                    >
                        <FaTruck className="menu-icon" />
                        Suppliers
                        {openMenu === "suppliers"
                            ? <FaChevronUp className="menu-caret" />
                            : <FaChevronDown className="menu-caret" />}
                    </li>

                    {openMenu === "suppliers" && (
                        <ul className="submenu">
                            <li
                                className={isActive("/suppliers") ? "active" : ""}
                                role="button"
                                tabIndex={0}
                                onClick={() => go("/suppliers")}
                                onKeyDown={(e) => keyActivate(e, () => go("/suppliers"))}
                            >
                                Supplier List
                            </li>

                            <li
                                className={isActive("/supplier-returns") ? "active" : ""}
                                role="button"
                                tabIndex={0}
                                onClick={() => go("/supplier-returns")}
                                onKeyDown={(e) => keyActivate(e, () => go("/supplier-returns"))}
                            >
                                Return Items
                            </li>
                        </ul>
                    )}

                    <li
                        className={isActive("/customers") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/customers")}
                        onKeyDown={(e) => keyActivate(e, () => go("/customers"))}
                    >
                        <FaUserFriends className="menu-icon" />
                        Customers
                    </li>

                    <li className={isActive("/products") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/products")}
                        onKeyDown={(e) => keyActivate(e, () => go("/products"))}>
                        <FaBoxOpen className="menu-icon" />
                        Products
                    </li>

                    <li className={isActive("/pos") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/pos")}
                        onKeyDown={(e) => keyActivate(e, () => go("/pos"))}>
                        <FaCashRegister className="menu-icon" />POS
                    </li>

                    {/* ORDERS DROPDOWN */}
                    <li
                        role="button"
                        tabIndex={0}
                        aria-expanded={openMenu === "orders"}
                        onClick={() => setOpenMenu(openMenu === "orders" ? null : "orders")}
                        onKeyDown={(e) => keyActivate(e, () => setOpenMenu(openMenu === "orders" ? null : "orders"))}
                    >
                        <FaClipboardList className="menu-icon" />
                        Orders
                        {openMenu === "orders"
                            ? <FaChevronUp className="menu-caret" />
                            : <FaChevronDown className="menu-caret" />}
                    </li>

                    {openMenu === "orders" && (
                        <ul className="submenu">
                            <li
                                className={isActive("/orders") ? "active" : ""}
                                role="button"
                                tabIndex={0}
                                onClick={() => go("/orders")}
                                onKeyDown={(e) => keyActivate(e, () => go("/orders"))}
                            >
                                Customer Orders List
                            </li>
                        </ul>
                    )}

                    <li
                        className={isActive("/expenses") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/expenses")}
                        onKeyDown={(e) => keyActivate(e, () => go("/expenses"))}
                    >
                        <FaMoneyBillWave className="menu-icon" />
                        Expenses
                    </li>

                    <li
                        className={isActive("/profit-loss") ? "active" : ""}
                        role="button"
                        tabIndex={0}
                        onClick={() => go("/profit-loss")}
                        onKeyDown={(e) => keyActivate(e, () => go("/profit-loss"))}
                    >
                        <FaBalanceScale className="menu-icon" />
                        Profit &amp; Loss
                    </li>

                    <li
                        className="logout"
                        role="button"
                        tabIndex={0}
                        onClick={logout}
                        onKeyDown={(e) => keyActivate(e, logout)}
                    >
                        <FaSignOutAlt className="menu-icon" />
                        Logout
                    </li>
                </ul>
            </div>

            {/* OVERLAY (only rendered when the mobile drawer is open) */}
            {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

            {/* MAIN */}
            <div className="main">

                <div className="topbar topbar-app">
                    <div className="topbar-app-left">
                        <button
                            className="menu-btn"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open menu"
                        >{String.fromCharCode(9776)}</button>
                        <div className="topbar-app-title">
                            <span className="app-title-label">Business Suite</span>
                            <h3>POS System</h3>
                        </div>
                    </div>

                    <div className="topbar-app-user">
                        <div className="user-chip" title={adminName}>
                            <span className="user-avatar">{initials}</span>
                            <span className="user-name">{adminName}</span>
                        </div>
                    </div>
                </div>

                {children}

            </div>
        </div>
    );
};

export default Layout;